import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BedtimeIcon from "@mui/icons-material/Bedtime";

import {
  StatCard,
  LineChart,
  DonutChart,
} from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  DashboardCardSkeleton,
  CohortGuard,
} from "../../../../../shared/components";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import {
  useRecoveryStats,
  useRecoveryTrends,
  useRecoveryScoreDistribution,
  useHeartRatePatterns,
} from "../hooks";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
} from "../../../../../shared/hooks";

const RECOVERY_DETAILS = {
  recoveryTrends: `Displays daily recovery metrics over time, illustrating changes in physiological readiness across the organisation. Recovery is typically derived from indicators such as heart rate variability (HRV), resting heart rate, and sleep quality, reflecting autonomic nervous system balance.

Tracking these trends helps identify sustained improvements, periods of reduced recovery, and potential fatigue accumulation, supporting proactive management of workload and wellbeing.`,
  recoveryScoreDistribution: `Displays the proportion of employees within each recovery range (e.g., low, moderate, high), providing a snapshot of overall workforce recovery status at a given time. Recovery scores reflect physiological readiness based on metrics such as HRV, resting heart rate, and sleep quality.

This distribution view helps identify how many employees may be well-recovered versus experiencing elevated fatigue or stress, supporting informed workload and wellbeing decisions.`,
  heartRatePatternsByTimeOfDay: `Displays how heart rate changes throughout the day, identifying peak and low periods across a 24-hour cycle. These fluctuations reflect variations in physical activity, metabolic demand, stress levels, and natural circadian rhythms.

Tracking these patterns helps distinguish expected daytime elevations from restful nighttime reductions, providing insight into daily workload, recovery periods, and overall cardiovascular regulation.`,
};

export const Recovery: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } =
    usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [trendView, setTrendView] = useState("recovery_score");

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      recovery_score: {
        min: typeof metric_min.recovery_score === "number" ? metric_min.recovery_score : undefined,
        max: typeof metric_max.recovery_score === "number" ? metric_max.recovery_score : undefined,
      },
      hrv: {
        min: typeof metric_min.hrv === "number" ? metric_min.hrv : undefined,
        max: typeof metric_max.hrv === "number" ? metric_max.hrv : undefined,
      },
      resting_heart_rate: {
        min: typeof metric_min.resting_heart_rate === "number" ? metric_min.resting_heart_rate : undefined,
        max: typeof metric_max.resting_heart_rate === "number" ? metric_max.resting_heart_rate : undefined,
      },
      sleep_performance: {
        min: typeof metric_min.sleep_performance === "number" ? metric_min.sleep_performance : undefined,
        max: typeof metric_max.sleep_performance === "number" ? metric_max.sleep_performance : undefined,
      },
      heart_rate: {
        min: typeof metric_min.heart_rate === "number" ? metric_min.heart_rate : undefined,
        max: typeof metric_max.heart_rate === "number" ? metric_max.heart_rate : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  const statsQuery = useRecoveryStats(filterParams);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;
  const trendsQuery = useRecoveryTrends({
    ...filterParams,
    metrics: [
      "recovery_score",
      "resting_heart_rate",
      "hrv",
      "sleep_performance",
    ],
    granularity,
  });
  const distributionQuery = useRecoveryScoreDistribution(filterParams);
  const patternsQuery = useHeartRatePatterns({
    ...filterParams,
    metric: "heart_rate_patterns",
    granularity: "hourly",
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    const transformed = transformTrendsData(trendsQuery.data, [
      "recovery_score",
      "resting_heart_rate",
      "hrv",
      "sleep_performance",
    ]);
    return transformed.map((item) => {
      const cleaned: Record<string, string | number> = {};
      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (value !== null) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    });
  }, [trendsQuery.data]);

  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color:
        item.name === "high" || item.label.includes("67-100")
          ? theme.palette.success.main
          : item.name === "medium" || item.label.includes("34-66")
          ? theme.palette.warning.main
          : theme.palette.error.main,
    }));
  }, [distributionQuery.data, theme]);

  const patternsData = useMemo(() => {
    if (!patternsQuery.data?.data?.patterns) return [];
    return patternsQuery.data.data.patterns
      .filter((pattern) => pattern.time)
      .map((pattern) => ({
        time: pattern.time!,
        value: pattern.value,
      }));
  }, [patternsQuery.data]);

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      {
        metric: stats.recovery_score,
        icon: FavoriteIcon,
      },
      {
        metric: stats.hrv,
        icon: MonitorHeartIcon,
      },
      {
        metric: stats.resting_heart_rate,
        icon: VolunteerActivismIcon,
      },
      {
        metric: stats.sleep_performance,
        icon: BedtimeIcon,
      },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const getTrendLabel = () => {
    switch (trendView) {
      case "recovery_score":
        return "Recovery Score";
      case "hrv":
        return "Heart Rate Variability";
      case "resting_heart_rate":
        return "Resting Heart Rate";
      case "sleep_performance":
        return "Sleep Performance";
      default:
        return "Recovery Score";
    }
  };

  const getYAxisMax = () => {
    switch (trendView) {
      case "recovery_score":
      case "sleep_performance":
        return 100;
      case "hrv":
        return 200;
      case "resting_heart_rate":
        return 100;
      default:
        return 100;
    }
  };

  const isLoading =
    statsQuery.isLoading ||
    trendsQuery.isLoading ||
    distributionQuery.isLoading ||
    patternsQuery.isLoading;

  const hasError =
    statsQuery.isError ||
    trendsQuery.isError ||
    distributionQuery.isError ||
    patternsQuery.isError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && (
          <Alert severity="error">
            Failed to load recovery data. Please try again later.
          </Alert>
        )}

        <Grid container spacing={3}>
          {isLoading ? (
            <>
              {[1, 2, 3, 4].map((index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <DashboardCardSkeleton />
                </Grid>
              ))}
            </>
          ) : (
            statCards.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))
          )}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Recovery Trends"
              subtitle="Daily trends showing recovery metrics over time."
              details={RECOVERY_DETAILS.recoveryTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setTrendView(newValue);
                  }}
                  size="small"
                >
                  <ToggleButton value="recovery_score">
                    Recovery Score
                  </ToggleButton>
                  <ToggleButton value="hrv">
                    Heart Rate Variability
                  </ToggleButton>
                  <ToggleButton value="resting_heart_rate">
                    Resting Heart Rate
                  </ToggleButton>
                  <ToggleButton value="sleep_performance">
                    Sleep Performance
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : trendsData.length > 0 ? (
                  <LineChart
                    data={trendsData}
                    xAxisKey="label"
                    series={[
                      {
                        dataKey: trendView,
                        label: getTrendLabel(),
                        color: theme.palette.success.main,
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[trendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[trendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No trend data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Recovery Score Distribution"
              subtitle="Proportion of employees in each recovery range."
              details={RECOVERY_DETAILS.recoveryScoreDistribution}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : distributionData.length > 0 ? (
                  <DonutChart data={distributionData} height={300} />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No distribution data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Heart Rate Patterns by Time of Day"
              subtitle="Variation in heart rate patterns throughout the day, highlighting peak and low points."
              details={RECOVERY_DETAILS.heartRatePatternsByTimeOfDay}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : patternsData.length > 0 ? (
                  <LineChart
                    data={patternsData}
                    xAxisKey="time"
                    series={[
                      {
                        dataKey: "value",
                        label: "Heart Rate",
                        color: theme.palette.warning.main,
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    showMinMaxLines
                    referenceMin={overallMinMax.heart_rate.min}
                    referenceMax={overallMinMax.heart_rate.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
